import accountValidation from './validation/accountValidation';
import accountFilterValidation from './validation/accountFilterValidation';
import useAccountResourceService, { AccountLitigationHoldDto } from 'domain/shared/account/accountResourceService';
import { HTTP } from 'framework/api';
import { ResourceData, ResourceId, useTimelineService } from 'framework/crud';
import { useDeliveryApi, useKernelApi } from 'infrastructure/api';

const useAccountManagementService = () => {
  const accountResourceService = useAccountResourceService();
  const { sendPostRequest, sendPatchRequest } = useKernelApi();
  const {
    sendGetRequest: sendDeliveryGetRequest,
    sendPatchRequest: sendDeliveryPatchRequest,
    sendPostRequest: sendDeliveryPostRequest,
  } = useDeliveryApi();

  const update = async (id: StringOrNumber, data: any) => {
    const preparedData = { ...data };
    delete preparedData.accountId;
    delete preparedData.isOnLitigationHold;
    delete preparedData.originalIsOnLitigationHold;
    delete preparedData.litigationHoldReason;
    delete preparedData.originalLitigationHoldReason;

    return accountResourceService.update(id, preparedData);
  };

  const getGlobalDeliveryPermissions = async (_id?: string): Promise<string[]> => {
    try {
      const response = await sendDeliveryGetRequest('/permissions');
      return response.json();
    } catch {
      return [];
    }
  };

  const getValidationSchema = () => Promise.resolve(accountValidation);

  const {
    search,
    create: createTimelineItem,
    getTimelinePageById,
  } = useTimelineService({
    parentEntityUrl: `/crm/accounts/:id`,
    useService: useKernelApi,
  });

  const searchTimelineItems = async (accId: StringOrNumber, requestData: any) => {
    const pinRespoce = await search(accId, {
      ...requestData,
      filter: {
        ...requestData?.filter,
        isPinned: {
          eq: true,
        },
      },
    });

    const unPinRespoce = await search(accId, {
      ...requestData,
      filter: {
        ...requestData?.filter,
        isPinned: {
          eq: false,
        },
      },
    });

    return {
      ...unPinRespoce,
      result: [...pinRespoce.result, ...unPinRespoce.result],
    };
  };

  const pinAccountTimeline = async (accountId: ResourceId, timelineId: ResourceId, pinStatus: boolean) => {
    const request = await sendPatchRequest(`/crm/accounts/${accountId}/timeline/${timelineId}`, {
      isPinned: pinStatus,
    });

    return request.json();
  };

  const getFilterValidationSchema = () => Promise.resolve(accountFilterValidation);

  const getOriginalName = async (id: StringOrNumber) => {
    const response = await sendPostRequest(`/crm/accounts/${id}/name/sensitive`);
    return response.json();
  };

  const getLitigationHold = async (accountAlias: string): Promise<AccountLitigationHoldDto | null> => {
    try {
      const response = await sendDeliveryPatchRequest(`/accounts/${accountAlias}`, {});
      return response.json();
    } catch (error) {
      if ((error as Response)?.status === HTTP.FORBIDDEN) {
        return null;
      }
      throw error;
    }
  };

  const patchLitigationHold = async (
    accountAlias: string,
    isOnLitigationHold: boolean | undefined,
    litigationHoldReason: string | undefined,
  ): Promise<void> => {
    const response = await sendDeliveryPatchRequest(`/accounts/${accountAlias}`, {
      isOnLitigationHold,
      litigationHoldReason,
    });
    await response.json();
  };

  const getClientPortalUsers = async (accountAlias: StringOrNumber) => {
    const response = await sendDeliveryGetRequest(`/accounts/${accountAlias}/client-portal/users`);
    return response.json();
  };

  const createPortalInvitation = async (accountAlias: StringOrNumber, data: ResourceData) => {
    const response = await sendDeliveryPostRequest(`/accounts/${accountAlias}/client-portal/users/invite`, data);
    return response.json();
  };

  const revokePortalInvitation = async (accountAlias: StringOrNumber, externalUserId: StringOrNumber) => {
    const response = await sendDeliveryPostRequest(
      `/accounts/${accountAlias}/client-portal/users/${externalUserId}/revoke-invitation`,
    );
    return response.json();
  };

  const activatePortalUser = async (accountAlias: StringOrNumber, externalUserId: StringOrNumber) => {
    const response = await sendDeliveryPostRequest(
      `/accounts/${accountAlias}/client-portal/users/${externalUserId}/activate`,
    );
    return response.json();
  };

  const deactivatePortalUser = async (accountAlias: StringOrNumber, externalUserId: StringOrNumber) => {
    const response = await sendDeliveryPostRequest(
      `/accounts/${accountAlias}/client-portal/users/${externalUserId}/deactivate`,
    );
    return response.json();
  };

  return {
    ...accountResourceService,
    createTimelineItem,
    getFilterValidationSchema,
    getGlobalDeliveryPermissions,
    getLitigationHold,
    getOriginalName,
    getTimelinePageById,
    getValidationSchema,
    patchLitigationHold,
    pinAccountTimeline,
    searchTimelineItems,
    update,
    getClientPortalUsers,
    createPortalInvitation,
    revokePortalInvitation,
    activatePortalUser,
    deactivatePortalUser,
  };
};

export default useAccountManagementService;
